import React, { useMemo } from "react";

/**
 * Phase 222 — draws an App Flow scene (primitive shapes laid out on the server) as an SVG.
 * The PowerPoint export draws the very same primitives, so both pictures match.
 */
const arrowPoints = (x, y, w, h, d) => {
  const hw = h / 2;
  let pts;
  if (d === "left") pts = [[x, y + hw], [x + w * 0.45, y], [x + w * 0.45, y + h * 0.25], [x + w, y + h * 0.25], [x + w, y + h * 0.75], [x + w * 0.45, y + h * 0.75], [x + w * 0.45, y + h]];
  else if (d === "right") pts = [[x + w, y + hw], [x + w * 0.55, y], [x + w * 0.55, y + h * 0.25], [x, y + h * 0.25], [x, y + h * 0.75], [x + w * 0.55, y + h * 0.75], [x + w * 0.55, y + h]];
  else if (d === "down") pts = [[x + w / 2, y + h], [x, y + h * 0.55], [x + w * 0.25, y + h * 0.55], [x + w * 0.25, y], [x + w * 0.75, y], [x + w * 0.75, y + h * 0.55], [x + w, y + h * 0.55]];
  else pts = [[x + w / 2, y], [x, y + h * 0.45], [x + w * 0.25, y + h * 0.45], [x + w * 0.25, y + h], [x + w * 0.75, y + h], [x + w * 0.75, y + h * 0.45], [x + w, y + h * 0.45]];
  return pts.map((p) => p.join(",")).join(" ");
};

export const SceneSvg = ({ scene, className = "", title }) => {
  const markerId = useMemo(() => `af-head-${Math.random().toString(36).slice(2, 8)}`, []);
  if (!scene) return null;
  const { w, h, shapes } = scene;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" role="img" aria-label={title || "Screen drawing"} className={className}
      style={{ fontFamily: "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif", display: "block" }} data-testid="appflow-scene">
      <defs>
        <marker id={markerId} markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto">
          <path d="M0,0 L10,5 L0,10 z" fill="#566376" />
        </marker>
      </defs>
      <rect width={w} height={h} fill="#F8FAFC" />
      {shapes.map((s, i) => {
        switch (s.t) {
          case "rect":
            return <rect key={i} x={s.x} y={s.y} width={s.w} height={s.h} rx={s.rx || 0} fill={s.fill || "none"} fillOpacity={s.alpha ?? 1}
              stroke={s.stroke || "none"} strokeWidth={s.stroke ? s.sw || 1 : 0} strokeDasharray={s.dash ? "6 4" : undefined} />;
          case "text":
            return <text key={i} x={s.x} y={s.y} fontSize={s.size} fill={s.fill} fontWeight={s.bold ? 700 : 400}
              textAnchor={s.anchor === "middle" ? "middle" : s.anchor === "end" ? "end" : "start"} dominantBaseline="central"
              fontFamily={s.font === "mono" ? "'IBM Plex Mono', ui-monospace, monospace" : undefined}>{s.s}</text>;
          case "circle":
            return <circle key={i} cx={s.cx} cy={s.cy} r={s.r} fill={s.fill || "none"} stroke={s.stroke || "none"} strokeWidth={s.stroke ? s.sw || 1 : 0} />;
          case "arrow":
            return <polygon key={i} points={arrowPoints(s.x, s.y, s.w, s.h, s.dir)} fill={s.fill} />;
          case "line":
            return <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.stroke} strokeWidth={s.sw || 2}
              strokeDasharray={s.dash ? "6 4" : undefined} markerEnd={s.head ? `url(#${markerId})` : undefined} />;
          default:
            return null;
        }
      })}
    </svg>
  );
};
